import React from 'react';

import { useIntl } from 'react-intl';

import { BaseCheckIcon } from 'assets/icons';

export interface IHorizontalStepperStep {
  id: string;
  /** Already translated. */
  label: string;
  /** A second line under the label. */
  date?: string | null;
  danger?: boolean;
  dataTest?: string;
}

interface IHorizontalStepperList {
  ariaLabel: string;
  /** Read out on the steps before the current one. */
  completedText: string;
  /** Read out on the steps after the current one. */
  upcomingText: string;
  dataTest?: string;
}

interface HorizontalStepperProps {
  /** Translation keys, or steps whose label is already translated. */
  steps: (string | IHorizontalStepperStep)[];
  currentStepIndex: number;
  /** Renders a named ordered list that spells out each step's state for screen readers. */
  list?: IHorizontalStepperList;
}

export const HorizontalStepper = ({ steps, currentStepIndex, list }: HorizontalStepperProps) => {
  const intl = useIntl();
  const Container = list ? 'ol' : 'div';
  const Item = list ? 'li' : 'div';

  return (
    <Container
      className={
        list
          ? 'd-flex flex-wrap align-items-center list-unstyled mb-0'
          : 'd-flex align-items-center'
      }
      aria-label={list?.ariaLabel}
      data-test={list?.dataTest ?? 'horizontal-stepper'}
    >
      {steps.map((step, index) => {
        const { id, label, date, danger, dataTest } =
          typeof step === 'string'
            ? {
                id: step,
                label: intl.formatMessage({ id: step }),
                date: null,
                danger: false,
                dataTest: undefined
              }
            : { ...step, danger: !!step.danger };
        const isCompleted = index < currentStepIndex;
        const isActive = index === currentStepIndex;
        const showConnector = index < steps.length - 1;
        let state = 'upcoming';
        if (isCompleted) state = 'completed';
        else if (isActive) state = 'current';

        let connectorColor = '#D1D5DB';
        if (isCompleted && index + 1 < currentStepIndex) connectorColor = '#9CA3AF';
        else if (isCompleted && index + 1 === currentStepIndex) connectorColor = '#185FA5';

        let activeClassName = isActive ? 'px-2 py-1 rounded-2' : 'py-1 px-0';
        if (isActive && danger) activeClassName += ' bg-danger-subtle';
        let markerClassName = !isCompleted && !isActive ? 'bg-white' : '';
        if (danger) markerClassName = 'bg-danger';

        const labelNode = (
          <span
            className={`text-nowrap ${isActive ? 'fw-medium' : 'fw-normal'}${danger ? ' text-danger' : ''}`}
            style={{
              fontSize: 12,
              color: danger ? undefined : isActive ? '#185FA5' : isCompleted ? '#475569' : '#94A3B8'
            }}
          >
            {label}
          </span>
        );

        return (
          <Item
            key={id}
            className="d-flex align-items-center"
            aria-current={list && isActive ? 'step' : undefined}
            data-test={dataTest}
            data-test-value={list ? state : undefined}
          >
            <div
              aria-current={!list && isActive ? 'step' : undefined}
              className={`d-flex align-items-center gap-1 ${activeClassName}`}
              style={isActive && !danger ? { backgroundColor: '#E6F1FB' } : undefined}
            >
              <div
                className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${markerClassName}`}
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: danger
                    ? undefined
                    : isCompleted
                      ? '#1D9E75'
                      : isActive
                        ? '#185FA5'
                        : undefined,
                  border: !isCompleted && !isActive && !danger ? '1.5px solid #CBD5E1' : 'none'
                }}
              >
                {isCompleted ? (
                  <BaseCheckIcon className="icon text-white" style={{ fontSize: '10px' }} />
                ) : (
                  <span
                    className={`fw-medium lh-1 ${isActive || danger ? 'text-white' : ''}`}
                    style={{
                      fontSize: 10,
                      color: !isActive && !danger ? '#94A3B8' : undefined
                    }}
                  >
                    {index + 1}
                  </span>
                )}
              </div>
              {date ? (
                <span className="d-flex flex-column">
                  {labelNode}
                  <span className="text-nowrap text-muted" style={{ fontSize: 11 }}>
                    {date}
                  </span>
                </span>
              ) : (
                labelNode
              )}
              {list && !isActive && (
                <span className="visually-hidden">
                  {isCompleted ? list.completedText : list.upcomingText}
                </span>
              )}
            </div>
            {showConnector && (
              <div
                style={{
                  width: 24,
                  height: 1,
                  backgroundColor: connectorColor,
                  margin: '0 12px',
                  flexShrink: 0
                }}
              />
            )}
          </Item>
        );
      })}
    </Container>
  );
};
